import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Building2 } from "lucide-react";
import { useI18n } from "../i18n";

export default function Header() {
  const { lang, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const base = `/${lang}`;
  const otherLang = lang === "ar" ? "en" : "ar";

  const links = [
    { to: base, label: t("header.nav.home"), id: "home" },
    { to: `${base}#how`, label: t("header.nav.how"), id: "how" },
    { to: `${base}#projects`, label: t("header.nav.projects"), id: "projects" },
    { to: `${base}#newsletter`, label: t("header.nav.newsletter"), id: "newsletter" },
  ];

  const switchLang = (target) => {
    // Rebuild path with new language prefix; preserve legal subpaths.
    const path = location.pathname;
    let newPath = `/${target}`;
    if (path.startsWith("/ar") || path.startsWith("/en")) {
      newPath = path.replace(/^\/(ar|en)/, `/${target}`);
    }
    setOpen(false);
    navigate(newPath + location.hash, { state: { languageSwitch: true } });
  };

  const handleAnchor = (e, id) => {
    e.preventDefault();
    setOpen(false);
    if (id === "home") {
      if (location.pathname !== base) {
        navigate(base);
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
      return;
    }
    if (location.pathname !== base) {
      navigate(base);
      // Wait for Home to mount, then scroll to target block
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ block: "start" });
      }, 60);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-white border-b border-gray-200"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between">
          <Link
            to={base}
            data-testid="header-brand-link"
            className="flex items-center gap-3 no-underline"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center bg-[#0C111D] text-white">
              <Building2 size={18} strokeWidth={1.75} />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif text-xl text-[#101828] tracking-tight">
                {t("header.brand")}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#475467] font-semibold">
                {t("header.brandTagline")}
              </span>
            </span>
          </Link>

          <nav
            className="hidden md:flex items-center gap-8"
            aria-label="Primary"
            data-testid="header-nav-desktop"
          >
            {links.map((l) => (
              <a
                key={l.id}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.id)}
                data-testid={`header-link-${l.id}`}
                className="text-sm font-medium text-[#101828] hover:text-[#1E3A8A] no-underline"
              >
                {l.label}
              </a>
            ))}
            <div
              className="flex items-center gap-1 border border-gray-300 px-1 py-1"
              role="group"
              aria-label={t("header.language")}
              data-testid="header-lang-switcher"
            >
              <button
                type="button"
                onClick={() => switchLang("ar")}
                data-testid="lang-switch-ar"
                className={`px-2 py-1 text-xs font-bold tracking-widest ${
                  lang === "ar"
                    ? "bg-[#0C111D] text-white"
                    : "bg-white text-[#101828] hover:bg-gray-100"
                }`}
              >
                {t("header.languageEs")}
              </button>
              <button
                type="button"
                onClick={() => switchLang("en")}
                data-testid="lang-switch-en"
                className={`px-2 py-1 text-xs font-bold tracking-widest ${
                  lang === "en"
                    ? "bg-[#0C111D] text-white"
                    : "bg-white text-[#101828] hover:bg-gray-100"
                }`}
              >
                {t("header.languageEn")}
              </button>
            </div>
          </nav>

          <button
            type="button"
            className="md:hidden inline-flex h-10 w-10 items-center justify-center border border-gray-300"
            aria-label={open ? t("header.menuClose") : t("header.menuOpen")}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="header-hamburger-btn"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-gray-200 bg-white"
          data-testid="header-nav-mobile"
        >
          <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.to}
                onClick={(e) => handleAnchor(e, l.id)}
                data-testid={`header-mobile-link-${l.id}`}
                className="py-3 text-base font-medium text-[#101828] border-b border-gray-100 no-underline"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-4">
              <span className="text-xs uppercase tracking-widest text-[#475467] font-semibold">
                {t("header.language")}
              </span>
              <button
                type="button"
                onClick={() => switchLang("ar")}
                data-testid="lang-switch-mobile-ar"
                className={`px-3 py-1 text-xs font-bold tracking-widest border ${
                  lang === "ar"
                    ? "bg-[#0C111D] text-white border-[#0C111D]"
                    : "bg-white text-[#101828] border-gray-300"
                }`}
              >
                {t("header.languageEs")}
              </button>
              <button
                type="button"
                onClick={() => switchLang("en")}
                data-testid="lang-switch-mobile-en"
                className={`px-3 py-1 text-xs font-bold tracking-widest border ${
                  lang === "en"
                    ? "bg-[#0C111D] text-white border-[#0C111D]"
                    : "bg-white text-[#101828] border-gray-300"
                }`}
              >
                {t("header.languageEn")}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
